// Versiones aprobadas del Plan de Emergencias v2 (PE-B1). Tabla emergency_plan_versions (M33) con su trigger de
// inmutabilidad: una versión 'aprobada' o 'reemplazada' no se cambia ni se borra; solo puede pasar de 'aprobada' a
// 'reemplazada'. M36 deja aprobar solo al administrador y congela los archivos de la carpeta de versiones.
// Al aprobar: la versión sube sola (PE-25), se guarda una foto completa (entradas, resultados, trazas, acciones y
// firmas) y el PDF sin marca de agua en emergency-files, en la carpeta de la empresa.
// Lo "por validar" no impide aprobar (va al anexo de pendientes del PDF).

import { supabase } from '../../supabaseClient';
import type { FilaAccion } from './pe-acciones';
import type { FilaPlan } from './pe-datos';
import type { SectorEnPantalla } from './pe-paso2';
import { calcularSector } from './pe-paso2';
import type { EntradasPaso3, Evaluacion } from './pe-paso3';
import type { RolFirmaPlan } from './pe-plan-pdf';

export const BUCKET_PLAN = 'emergency-files';
export const SEGUNDOS_URL_VERSION = 120;
export const VERSION_MOTOR = 'pe-v2 PE-B1';
export const ROLES_FIRMA: RolFirmaPlan[] = ['elabora', 'representante_legal'];

export interface FirmaGuardada { rol: RolFirmaPlan; nombre: string; cargo: string | null; firma_path: string; firmado_en: string }

export interface FilaVersion {
  id: string;
  company_id: string;
  plan_id: string;
  version: number;
  code: string | null;
  status: 'borrador' | 'aprobada' | 'reemplazada';
  snapshot: SnapshotPlan | Record<string, never>;
  engine_version: string | null;
  pdf_path: string | null;
  approved_by: string | null;
  approved_at: string | null;
  signatures: FirmaGuardada[];
  created_at: string;
}

export interface SnapshotPlan {
  esquema: 1;
  empresa: string;
  plan: FilaPlan;
  sectores: SectorEnPantalla[];
  entradas: EntradasPaso3;
  evaluaciones: Evaluacion[];
  acciones: FilaAccion[];
  brigadaMiembros: number | null;
  firmas: FirmaGuardada[];
  generado_en: string;
}

// Foto completa e independiente de los datos de la pantalla: copia profunda, así lo que cambie después no la toca.
export function armarSnapshot(d: Omit<SnapshotPlan, 'esquema' | 'generado_en'>, ahora = new Date()): SnapshotPlan {
  return JSON.parse(JSON.stringify({ esquema: 1, ...d, generado_en: ahora.toISOString() })) as SnapshotPlan;
}

export const siguienteVersion = (plan: Pick<FilaPlan, 'current_version'>, versiones: Pick<FilaVersion, 'version'>[]): number =>
  Math.max(plan.current_version ?? 0, ...versiones.map(v => v.version), 0) + 1;

// Ruta en emergency-files: la primera carpeta es la empresa (políticas ef_*); "versiones" la congela M36.
export const rutaVersion = (companyId: string, planId: string, version: number, archivo: string) =>
  `${companyId}/planes/${planId}/versiones/v${version}/${archivo}`;

export interface FirmaEnPantalla { rol: RolFirmaPlan; nombre: string; cargo: string; conTrazo: boolean }

// Prefijos de los textos por capítulo (PE-C1): faltantesPorCapitulo (pe-capitulos) reparte con ellos.
export const PREFIJO_CAPITULO_1 = 'Capítulo 1 (Datos generales):';
export const PREFIJO_CAPITULO_2 = 'Capítulo 2 (Sectores y carga de fuego):';

// Qué falta para aprobar (vacío = se puede). Datos mínimos de los capítulos 1 y 2 (antes, pasos 1 y 2); cada
// incumplimiento con su acción obligatoria guardada con responsable y fecha; las dos firmas.
export function faltantesAprobacion(d: {
  plan: FilaPlan | null; sectores: SectorEnPantalla[]; evaluaciones: Evaluacion[]; acciones: FilaAccion[]; firmas: FirmaEnPantalla[];
}): string[] {
  const f: string[] = [];
  const p = d.plan;
  if (!p) return ['Falta guardar los datos generales (capítulo 1).'];
  const paso1: [boolean, string][] = [
    [!!p.code?.trim(), 'el código del plan'], [!!p.canton_code, 'el cantón'], [p.site_area_m2 != null, 'el área total'],
    [!!p.site_occupancy, 'la ocupación o actividad principal'], [p.site_workers != null, 'el número de trabajadores'],
  ];
  const faltan1 = paso1.filter(([ok]) => !ok).map(([, t]) => t);
  if (faltan1.length) f.push(`${PREFIJO_CAPITULO_1} falta ${faltan1.join(', ')}.`);
  if (d.sectores.length === 0) f.push(`${PREFIJO_CAPITULO_2} falta al menos un sector con su carga de fuego.`);
  else {
    const malos = d.sectores.filter(s => !calcularSector(s).ok).map(s => s.sector.name);
    if (malos.length) f.push(`${PREFIJO_CAPITULO_2} la carga de fuego no se puede calcular en ${malos.join(', ')}.`);
  }
  const guardadas = new Map(d.acciones.filter(a => a.source_key).map(a => [a.source_key as string, a]));
  const vistas = new Set<string>();
  for (const a of d.evaluaciones.flatMap(e => e.acciones)) {
    if (vistas.has(a.sourceKey)) continue;
    vistas.add(a.sourceKey);
    const g = guardadas.get(a.sourceKey);
    if (!g || !(g.responsible_worker_id || g.responsible_role?.trim()) || !g.due_date) {
      f.push(`Incumplimiento sin acción: "${a.articulo}" necesita su acción obligatoria guardada con responsable y fecha (Revisión y aprobación › Plan de acción).`);
    }
  }
  for (const rol of ROLES_FIRMA) {
    const x = d.firmas.find(y => y.rol === rol);
    const etiqueta = rol === 'elabora' ? 'de quien elabora (responsable de SST)' : 'del representante legal';
    if (!x?.nombre.trim()) f.push(`Falta el nombre ${etiqueta}.`);
    if (!x?.conTrazo) f.push(`Falta la firma ${etiqueta}.`);
  }
  return f;
}

const falla = (accion: string, e: { message?: string; code?: string } | null) => {
  if (e?.code === '42501') throw new Error(`No tienes permiso para ${accion}: aprobar versiones es solo del administrador.`);
  throw new Error(`No se pudo ${accion}: ${e?.message ?? 'error desconocido'}.`);
};

export async function cargarVersiones(planId: string): Promise<FilaVersion[]> {
  const { data, error } = await supabase.from('emergency_plan_versions').select('*').eq('plan_id', planId).order('version', { ascending: false });
  if (error) falla('leer las versiones del plan', error);
  return (data ?? []) as FilaVersion[];
}

export interface Aprobacion {
  companyId: string;
  plan: FilaPlan;
  version: number;
  pdf: Blob;
  firmas: { rol: RolFirmaPlan; nombre: string; cargo: string; png: Blob; firmadoEn: string }[];
  snapshot: Omit<SnapshotPlan, 'firmas' | 'esquema' | 'generado_en'>;
  usuarioId: string | null;
  anterior: FilaVersion | null; // versión aprobada vigente (pasa a 'reemplazada')
}

// Sube firmas y PDF (sin sobrescribir), crea la versión aprobada y después marca la anterior como reemplazada y
// sube current_version del plan. Si la fila no se crea, intenta quitar los archivos subidos.
export async function aprobarVersion(a: Aprobacion): Promise<FilaVersion> {
  const subidos: string[] = [];
  const subir = async (ruta: string, blob: Blob, tipo: string) => {
    const { error } = await supabase.storage.from(BUCKET_PLAN).upload(ruta, blob, { contentType: tipo, upsert: false });
    if (error) falla('subir el archivo de la versión', error);
    subidos.push(ruta);
  };
  try {
    const firmas: FirmaGuardada[] = [];
    for (const f of a.firmas) {
      const ruta = rutaVersion(a.companyId, a.plan.id, a.version, `firma_${f.rol}.png`);
      await subir(ruta, f.png, 'image/png');
      firmas.push({ rol: f.rol, nombre: f.nombre.trim(), cargo: f.cargo.trim() || null, firma_path: ruta, firmado_en: f.firmadoEn });
    }
    const pdfPath = rutaVersion(a.companyId, a.plan.id, a.version, 'plan.pdf');
    await subir(pdfPath, a.pdf, 'application/pdf');
    const snapshot = armarSnapshot({ ...a.snapshot, firmas });
    const { data, error } = await supabase.from('emergency_plan_versions').insert({
      company_id: a.companyId, plan_id: a.plan.id, version: a.version, code: a.plan.code, status: 'aprobada', snapshot,
      engine_version: VERSION_MOTOR, pdf_path: pdfPath, approved_by: a.usuarioId, approved_at: new Date().toISOString(),
      signatures: firmas, created_by: a.usuarioId,
    }).select('*');
    if (error || !data?.length) falla('guardar la versión aprobada', error ?? { message: 'sin permiso (RLS)' });
    const fila = (data as FilaVersion[])[0];
    if (a.anterior) {
      const { error: e2 } = await supabase.from('emergency_plan_versions').update({ status: 'reemplazada' }).eq('id', a.anterior.id);
      if (e2) console.error('No se pudo marcar como reemplazada la versión anterior', e2);
    }
    const { error: e3 } = await supabase.from('emergency_plans').update({ current_version: a.version, updated_at: new Date().toISOString() }).eq('id', a.plan.id);
    if (e3) console.error('No se pudo actualizar current_version del plan', e3);
    return fila;
  } catch (e) {
    if (subidos.length) await supabase.storage.from(BUCKET_PLAN).remove(subidos).catch(() => {});
    throw e;
  }
}

// URL firmada de 120 s que pide la descarga (Content-Disposition: attachment): en el celular se descarga.
export async function urlDescargaVersion(ruta: string, nombre: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_PLAN).createSignedUrl(ruta, SEGUNDOS_URL_VERSION, { download: nombre });
  if (error || !data?.signedUrl) falla('preparar la descarga', error);
  return (data as { signedUrl: string }).signedUrl;
}

export const versionVigente = (versiones: FilaVersion[]): FilaVersion | null => versiones.find(v => v.status === 'aprobada') ?? null;

// ── Estado del plan (PE-A4b) ──
// Huella de lo que el responsable escribe en el plan: plan (sin lo que cambia solo al aprobar), sectores con su carga de
// fuego, entradas del paso 3 y acciones. Sin ids, marcas de tiempo ni quién creó; las evaluaciones no entran porque se
// derivan de las entradas. Se compara la de la pantalla con la de la foto de la versión vigente.
const SIN_HUELLA = new Set(['id', 'company_id', 'plan_id', 'sector_id', 'created_by', 'closed_by']);
// F1a (M40): site_id tampoco entra. La base lo pone (la Matriz) y las fotos aprobadas antes de M40 no lo tienen: si
// contara, todo plan aprobado saldría "con cambios sin aprobar" al aplicar M40.
const SIN_HUELLA_PLAN = new Set(['current_version', 'status', 'site_id']);
// PE-A4c: normaliza de forma recursiva. {}, [], null, undefined y la clave ausente son lo mismo ("sin datos"): una clave
// cuyo valor normalizado queda vacío se quita, así { "<sector>": { medios: {}, factores: {} } } queda igual a {} (fila
// MESERI vacía que crea "Guardar cálculos"). 0, false y '' son datos. Las claves se ordenan para que el orden no cuente;
// dentro de una lista, un elemento vacío queda como null para no correr las posiciones.
export function normalizarHuella(x: unknown, quitar: Set<string> = SIN_HUELLA): unknown {
  if (x === null || x === undefined) return undefined;
  if (Array.isArray(x)) {
    const lista = x.map(y => normalizarHuella(y) ?? null);
    return lista.some(y => y !== null) ? lista : undefined;
  }
  if (typeof x === 'object') {
    const pares = Object.keys(x as object).sort()
      .filter(k => !quitar.has(k) && !SIN_HUELLA.has(k) && !k.endsWith('_at'))
      .map(k => [k, normalizarHuella((x as Record<string, unknown>)[k])] as const)
      .filter(([, v]) => v !== undefined);
    return pares.length ? Object.fromEntries(pares) : undefined;
  }
  return x;
}
export function huellaPlan(d: Pick<SnapshotPlan, 'plan' | 'sectores' | 'entradas' | 'acciones'>): string {
  return JSON.stringify({
    plan: normalizarHuella(d.plan, SIN_HUELLA_PLAN) ?? null, sectores: normalizarHuella(d.sectores) ?? null,
    entradas: normalizarHuella(d.entradas) ?? null, acciones: normalizarHuella(d.acciones) ?? null,
  });
}

// "Borrador" sin versión aprobada; "Aprobado (versión N)"; y si la pantalla ya no coincide con la foto de la versión
// vigente, "Aprobado (versión N) con cambios sin aprobar".
export function estadoDelPlan(vigente: Pick<FilaVersion, 'version' | 'snapshot'> | null,
  actual: Pick<SnapshotPlan, 'plan' | 'sectores' | 'entradas' | 'acciones'> | null): string {
  if (!vigente) return 'Borrador';
  const s = vigente.snapshot as Partial<SnapshotPlan>;
  const conCambios = !!actual && !!s.plan && huellaPlan(s as SnapshotPlan) !== huellaPlan(actual);
  return `Aprobado (versión ${vigente.version})${conCambios ? ' con cambios sin aprobar' : ''}`;
}
